import React from 'react';
import { findDOMNode } from "react-dom";
import { TweenMax } from 'gsap';
import ScrollMagic from "./plugins/ScrollMagic";
import * as Sentry from '@sentry/browser';
import CryptoJS from 'crypto-js';
import { SECRET_PASS, SERVICE_FIBRE, SERVICE_NBN, SERVICE_FLIP_TO_FIBRE } from './constants';

export default {

    convertToSlug(text) {
        return text
            .toLowerCase()
            .replace(/^\/|\/$/g, '')
            .replace(/\//g, '-')
            .replace(/[^\w-]+/g,'');
    },

    getSubAnimation(el) {
        return {backgroundPositionY: (el.clientHeight * 2) + "px"};
    },

    animatePageIn(container) {
        TweenMax.to(container, 1, {opacity: 1});
    },

    genericAnimation(component) {
        let controller = new ScrollMagic.Controller(),
            node = findDOMNode(component);

        new ScrollMagic.Scene({
            triggerElement: node,
            offset: '-100px',
            reverse: false
        })
            .setTween(TweenMax.fromTo(node, 1, {opacity: 0}, {opacity: 1}))
            .addTo(controller);

        return controller;
    },

    destroy(controller) {
        controller.destroy(true);
    },

	addDecimals(input, addDollarSign = false) {
		let value = Number(String(input).replace('$', '')),
			res = String(input).split("."),
			result = '';

		if (res.length > 1 && Number(res[1]) > 0) {
			result = value.toFixed(2);
		} else {
			result = String(value);
		}

		return addDollarSign ? `$${result}` : result;
	},

    noWidows(text) {
        if (text) {
            return text.replace(/\s(?=[^\s]*$)/g, "&nbsp;");
        }
    },

    getGenericErrorMsg() {
        return 'An error has occurred. Our team will put down their coffees and look into this right away.';
    },

    alphaNumeric(string) {
        return string.replace(/[^a-zA-Z0-9 ]/g, "");
    },

	slugify(str) {
		str = str.replace(/^\s+|\s+$/g, ''); // trim
		str = str.toLowerCase();
		str = str.replace(/<[^>]+>/g, '');

		// remove accents, swap ñ for n, etc
		let from = "àáäâèéëêìíïîòóöôùúüûñç·/_,:;";
		let to   = "aaaaeeeeiiiioooouuuunc------";
		for (let i = 0, l = from.length; i<l; i++) {
			str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
		}

		str = str.replace(/[^a-z0-9 -]/g, '') // remove invalid chars
			.replace(/\s+/g, '-') // collapse whitespace and replace by -
			.replace(/-+/g, '-'); // collapse dashes

		return str;
	},

    handleError(error, errorInfo) {
        console.error(error);
    	if (process.env.NODE_ENV === 'production') {
			Sentry.captureMessage(error);
		}
    },

	handleException(error, errorInfo) {
        console.error(error);
		if (process.env.NODE_ENV === 'production') {
			Sentry.captureException(error);
		}
	},

    /*
        Handle custom Google Tag Manager events by passing the name
    */
    pushEvent(name, values = {}) {
    	let data = {};

    	if (Object.keys(values).length > 0) {
    		data = {...values, event: name}
		} else {
    		data = {event: name};
		}

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(data);
    },

 /*
		Handle GA4 events Google Tag Manager events by passing the name
	*/
	pushGA4Events(name, values = {}) {
		let data = {};

		if (Object.keys(values).length > 0) {
			data = {...values, event: name}
		} else {
			data = {event: name};
		}

		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push(data);
	},

	/*
		Replicate a "page view" using hotjars hj() functionality
		Url example: /some/path
	*/
    pushHJEvent(url) {
    	if (!url.startsWith('/')) {
    		console.error('Hotjar event must begin with a forward slash.');
		} else {
			if (typeof hj === 'function') {
				hj('vpv', url);
			}
		}
	},

	getLocalVar(name, value = '') {
    	return (localStorage[name] ? localStorage[name]: value);
	},

	setLocalVar(name, value) {
		localStorage[name] = value;
	},

	getSessionVar(name, value = '') {
		return (sessionStorage[name] ? sessionStorage[name]: value);
	},

    setSessionVar(name, value) {
		sessionStorage[name] = value;
	},

	convertTitle(planSpeed) {
		switch (planSpeed) {
			case "nbn50":
				return {
					title: "Home Business nbn™",
					desc: "Home-based businesses",
				};
			case "nbn100":
				return {
					title: "Start-up nbn™",
					desc: "Growing businesses seeking great value",
				};
			case "nbn250":
				return {
					title: "Accelerator nbn™",
					desc: "Small businesses reliant on the cloud",
				};
			case "nbn500":
				return {
					title: "Performance nbn™",
					desc: "Robust offices with heavy usage",
				};
			case "nbn1000":
				return {
					title: "Power Move™",
					desc: "Businesses moving to the next stage",
				};
			default:
				break;
		}
    },
    
    encryptData (string) {
        const data = CryptoJS.AES.encrypt(
            JSON.stringify(string),
            SECRET_PASS
        ).toString()
    
        return data
    },

    processAddressesResponse(data) {
        return data.reduce((addressList, element) => {
            if (element.providers && element.providers.length > 0) {
                addressList.push({
                    address: element.formattedAddress,
                    locationId: element.providers.map(provider => provider.locationId),
                    name: element.providers.map(provider => provider.name)
                    // .filter(item => item.name === 'Uniti' || item.name === 'NBN')
                })
            }
            return addressList
        }, [])
    },

    getSqFromSessionStorage() {
        const sqResult = this.getSessionVar('sq');
        const sqObject = sqResult.length > 0 ? JSON.parse(sqResult) : {}

        return sqObject
    },

    sqAddressResult (address) {
        const selectedAddress = (typeof address === 'object') ? address.address: address
        const addressArray = selectedAddress.split(' ')
        try {
            if (addressArray.length >= 6 && addressArray[addressArray.length - 1] !== '') {
                addressArray
                    .slice(0, addressArray.length - 2)
                    .join(' ')
                    .split(',')[1]
                    .slice(1)
                const userPostcode = addressArray[addressArray.length - 1]
                const userState = addressArray[addressArray.length - 2]
                const userSuburb = addressArray.slice(0, addressArray.length - 2).join(' ').split(',')[1].slice(1) // If it is business and has unit, we have to manually change it so we can pick the correct suburb
                return {
                    userPostcode: userPostcode.toLowerCase(),
                    userSuburb: userSuburb.toLowerCase(),
                    userState: userState.toLowerCase(),
                }
            }
        } catch (error) {
            return {userPostcode: '', userSuburb: '', userState: ''}
        }
    },

    pushEventClickLogo() {
        this.pushGA4Events('gtm-SiteNavClickLogo',
            {
                eventInfo: {
                    clickUrl: window.location.origin,
                    pageCategory: 'Business'
                }
            })
    },

    buildSelectItems(order) {
        let items = [];

        items.push(this.buildBroadbandItem(order))
        if (order.payg_phone || order.unlimited_phone) {
          items.push(this.buildVoipEvent(order))
        }
        if (order.modem) {
          items.push(this.buildModemEvent(order))
        }
        if (order.extender) {
            items.push(this.buildExtenderEvent(order))
        }

        if (order.interimPlanName) {
            items.push(this.buildInterimPlanEvent(order))
        }

        return items;
    },

    mapCategory(order){
        if (order.type === 'broadband') {
            if (order.technology === 'fibre') {
                if (order.flip) {
                    return 'f2f'
                }
            } else if (order.technology === 'nbn') {
                return SERVICE_NBN
            }
        }
    
        return SERVICE_NBN
    },

    buildBroadbandItem(order){
        return {
            item_name: order.planName,
            item_category: 'Business',
            item_category2: this.mapCategory(order),
            item_category3: order.payg_phone || order.unlimited_phone || order.modem || order.extender ? 'Bundle' : 'none',
            price: order.price,
            currency: 'aud',
            quantity: 1
        }
    },

    buildVoipEvent(order) {
        return {
            item_name: `VoIP ${order.payg_phone ? 'Pay-as-you-go' : 'Unlimited'}`,
            item_category: 'Business',
            item_category2: 'Business Phone',
            item_category3: 'Bundle',
            price: order.payg_phone ? 0 : 10,
            quantity: 1
        }
    },

    buildModemEvent() {
        return {
            item_name: 'ZTE H1600',
            item_category: 'Business',
            item_category2: 'Router',
            item_category3: 'Bundle',
            price: 0,
            currency: 'aud',
            quantity: 1
        }
    },

    buildInterimPlanEvent(order) {
        return {
            item_name: order.interimPlanName,
            item_category: 'Business',
            item_category2: 'Interim Plan',
            item_category3: 'Bundle',
            price: order.interimPlanPrice,
            currency: 'aud',
            quantity: 1
        }
    },

    buildExtenderEvent(order) {
        return {
            item_name: 'ZTE h3601 extender',
            item_category: 'Business',
            item_category2: 'Extender',
            item_category3: 'Bundle',
            price: 100,
            currency: 'aud',
            quantity: order.extenderQuantity ? order.extenderQuantity : 1,
        }
    },

    pushEventSelectItem(order) {

        this.pushGA4Events('select_item', {
            ecommerce: {
                currency: "aud",
                value: order.totalMinCost,
                ...{ items: [this.buildSelectItems(order)] }
            }
        })
    },

    pushEventNbnOrderNowBusiness(data) {
        this.pushGA4Events('nbnOrderNowBusiness', {
            eventInfo: {
                planName: data.planName, 
                businessPhonePlan : data.payg_phone ? 'Pay As You Go' : (data.unlimited_phone ? '$10/mth Unlimited call pack' : null),
                modemName: data.modem ? 'ZTE Modem' : null,
                extenderName: data.extender ? 'ZTE Wifi Extender' : null,
                orderType: data.isFlipToFibrePage ? 'F2F' : 'NBN',
                totalPrice: data.totalMinCost,
                pageCategory: "Business",
                ...(data.isFlipToFibrePage && {interimPlanName: data.interimPlanName})
            }
        })
    }
    
}


// WEBPACK FOOTER //
// ./resources/assets/js/globals.js